'use client';

import {
    Button,
    Modal,
    Select,
    Stack,
    TextInput,
    Textarea,
    Group,
    Checkbox,
    Switch,
} from '@mantine/core';
import { useState } from 'react';
import { Dropzone } from '../../EditorText/Dropzone';
import { sliceDefinitions } from '@/src/types/sliceDefinitions';
import styles from './AddSliceModal.module.scss';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import { FullSizeLoader } from '../../Loader/FullSizeLoader';

export const AddSliceModal = ({
    onAdd,
    opened,
    onClose,
}: {
    onAdd: (slice: Record<string, any>) => void;
    opened: boolean;
    onClose: () => void;
}) => {
    const [type, setType] = useState<string | null>(null);
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [arrayItems, setArrayItems] = useState<Record<string, any[]>>({});

    const handleFieldChange = (key: string, value: any) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleArrayItemChange = (
        fieldKey: string,
        index: number,
        itemKey: string,
        value: any
    ) => {
        setArrayItems((prev) => {
            const items = [...(prev[fieldKey] || [])];
            if (!items[index]) items[index] = itemKey ? {} : '';
            if (itemKey) {
                items[index][itemKey] = value;
            } else {
                items[index] = value;
            }
            return { ...prev, [fieldKey]: items };
        });
    };

    const addArrayItem = (fieldKey: string) => {
        setArrayItems((prev) => ({
            ...prev,
            [fieldKey]: [...(prev[fieldKey] || []), {}],
        }));
    };

    const handleSubmit = () => {
        if (!type) return;
        // @ts-ignore
        const arrayField = sliceDefinitions[type]?.arrayField;
        const arrayPayload = arrayField
            ? { [arrayField.key]: arrayItems[arrayField.key] || [] }
            : {};
        onAdd({ type, ...formData, ...arrayPayload });
        onClose();
        setFormData({});
        setArrayItems({});
        setType(null);
    };

    // @ts-ignore
    const fields = type ? sliceDefinitions[type]?.fields ?? [] : [];
    // @ts-ignore
    const arrayField = type ? sliceDefinitions[type]?.arrayField : undefined;

    const renderField = (field: any, value: any, onChange: (v: any) => void) => {
        if (field.type === 'image') {
            return (
                <Dropzone
                    imageUrl={value || ''}
                    setImageUrl={(url) => onChange(url)}
                />
            );
        } else if (field.type === 'textarea') {
            return (
                <Textarea
                    value={value || ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
        } else if (field.type === 'boolean') {
            return (
                <Checkbox
                    label={field.label}
                    checked={value ?? false}
                    onChange={(e) => onChange(e.currentTarget.checked)}
                />
            );
        } else if (field.key === 'version') {
            return (
                <Switch
                    label="Thème sombre"
                    checked={value === 'dark'}
                    onChange={(e) => onChange(e.currentTarget.checked ? 'dark' : 'light')}
                />
            );
        } else {
            return (
                <TextInput
                    value={value || ''}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            );
        }
    };

    return (
        <Modal opened={opened} onClose={onClose} title="Ajouter un bloc" size="lg" centered className={styles.modal}>
            <Stack>
                <Select
                    label="Type de slice"
                    placeholder="Choisir un type"
                    data={Object.entries(sliceDefinitions).map(([value, def]) => ({
                        value,
                        label: def.label,
                    }))}
                    value={type}
                    onChange={(val) => setType(val)}
                />


                {fields.map((field: any) => (
                    <div key={field.key}>
                        {field.type !== 'boolean' && field.key !== 'theme' && (
                            <label>{field.label}</label>
                        )}
                        {renderField(field, formData[field.key], (val) =>
                            handleFieldChange(field.key, val)
                        )}
                    </div>
                ))}

                {arrayField && (
                    <div>
                        <h4>{arrayField.label}</h4>

                        {(arrayItems[arrayField.key] || []).map((item, index) => (
                            <Stack
                                key={index}
                                style={{
                                    border: '1px solid #ccc',
                                    padding: '1rem',
                                    marginBottom: '1rem',
                                }}
                            >
                                {/* ✅ SI arrayField.fields existe */}
                                {arrayField.fields ? (
                                    arrayField.fields.map((field: any) => (
                                        <div key={field.key}>
                                            <label>{field.label}</label>
                                            {renderField(field, item[field.key], (val) =>
                                                handleArrayItemChange(arrayField.key, index, field.key, val)
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    // ✅ Sinon, assume un array de strings (ex: images)
                                    <div>
                                        <label>Image</label>
                                        {renderField({ type: 'image' }, item, (val) =>
                                            handleArrayItemChange(arrayField.key, index, '', val)
                                        )}
                                    </div>
                                )}
                            </Stack>
                        ))}

                        <Button
                            size="xs"
                            variant="outline"
                            onClick={() => addArrayItem(arrayField.key)}
                        >
                            ➕ Ajouter un élément
                        </Button>
                    </div>
                )}


                <Group justify="end">
                    <Button onClick={handleSubmit} disabled={!type}>
                        Ajouter
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
